import { useTranslation } from '@jupiter/plugin-runtime/i18n';
import { useState, useEffect, useMemo } from 'react';

interface PageData<T> {
  error?: boolean;
  total: number;
  items: T[];
}

export interface UseListPanelOptions<T> {
  id: number;
  pagination?: {
    defaultPageSize?: number;
  };
  fetch: (
    id: number,
    pageNumber: number,
    pageSize: number,
  ) => PageData<T> | Promise<PageData<T>>;
}

export const useListPanel = <T>({
  id,
  pagination = {},
  fetch,
}: UseListPanelOptions<T>) => {
  const { i18n } = useTranslation();
  const { defaultPageSize = 8 } = pagination;
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(
    useMemo(() => {
      const data = fetch(id, 1, defaultPageSize);
      return typeof (data as PageData<T>).items === 'undefined';
    }, []),
  );
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<T[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const loadData = async (page: number) => {
    const data = fetch(id, page, defaultPageSize);

    setError(false);

    if (typeof (data as PageData<T>).items !== 'undefined') {
      const { total, items } = data as PageData<T>;
      setLoading(false);
      setTotal(total);
      setItems(items);
    } else {
      setLoading(true);
      const { error, total, items } = await data;

      setLoading(false);
      setError(error === true);
      setTotal(total);
      setItems(items);
    }
  };
  const handleChange = (newPageNumber: number) => {
    setPageNumber(newPageNumber);
    loadData(newPageNumber);
  };

  useEffect(() => {
    setTotal(0);
    handleChange(1);
  }, [id]);

  useEffect(() => {
    loadData(pageNumber);
  }, [i18n.language]);

  return {
    id,
    loading,
    error,
    items,
    defaultPageSize,
    pageNumber,
    total,
    handleChange,
  };
};
